import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Today as TodayIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  Search as SearchIcon,
  DateRange as DateRangeIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { format } from 'date-fns';
import {
  ChartPeriod,
  fetchPlatformStats,
  formatChartDate,
  PlatformStats,
} from './platformStatsShared';

const PIE_COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#0288d1', '#d32f2f', '#6d4c41'];

type ActivityFilter = 'all' | 'active7d' | 'active30d' | 'inactive30d';

const BusinessReports: React.FC = () => {
  const theme = useTheme();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartPeriod, setChartPeriod] = useState<ChartPeriod>('daily');
  const [search, setSearch] = useState('');
  const [dvlaSearch, setDvlaSearch] = useState('');
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>('all');

  useEffect(() => {
    fetchPlatformStats()
      .then(setStats)
      .catch(err => {
        setError(err.message || 'Failed to load reports');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  const chartData = useMemo(() => {
    if (!stats) return [];
    const src =
      chartPeriod === 'daily' ? stats.daily
      : chartPeriod === 'weekly' ? stats.weekly
      : stats.monthly;
    return src.map(r => ({
      label: formatChartDate(
        chartPeriod === 'weekly' ? (r as { week: string }).week : (r as { date?: string; month?: string }).date ?? (r as { month: string }).month,
        chartPeriod
      ),
      Checks: r.count,
    }));
  }, [stats, chartPeriod]);

  const topBusinesses = useMemo(() => {
    if (!stats) return [];
    return [...stats.businesses]
      .sort((a, b) => b.completedChecks - a.completedChecks)
      .slice(0, 15)
      .map(b => ({
        name: b.name?.length > 22 ? b.name.slice(0, 20) + '…' : b.name,
        checks: b.completedChecks,
      }));
  }, [stats]);

  const topDvlaBusinesses = useMemo(() => {
    if (!stats?.dvlaBusinesses) return [];
    return [...stats.dvlaBusinesses]
      .filter(b => b.completedDvlaChecks > 0)
      .sort((a, b) => b.completedDvlaChecks - a.completedDvlaChecks)
      .slice(0, 12)
      .map(b => ({
        name: b.name?.length > 22 ? b.name.slice(0, 20) + '…' : b.name,
        checks: b.completedDvlaChecks,
      }));
  }, [stats]);

  const filteredDvlaBusinesses = useMemo(() => {
    if (!stats?.dvlaBusinesses) return [];
    const query = dvlaSearch.trim().toLowerCase();
    return stats.dvlaBusinesses
      .filter(b => {
        if (!query) return true;
        return (b.name || '').toLowerCase().includes(query) || (b.status || '').toLowerCase().includes(query);
      })
      .sort((a, b) => b.completedDvlaChecks - a.completedDvlaChecks || b.totalDvlaChecks - a.totalDvlaChecks);
  }, [stats, dvlaSearch]);

  const filteredBusinesses = useMemo(() => {
    if (!stats) return [];
    const query = search.trim().toLowerCase();
    return stats.businesses
      .filter(b => {
        if (activityFilter === 'active7d' && b.checksWeek === 0) return false;
        if (activityFilter === 'active30d' && b.checksMonth === 0) return false;
        if (activityFilter === 'inactive30d' && b.checksMonth > 0) return false;
        if (!query) return true;
        return (b.name || '').toLowerCase().includes(query) || (b.status || '').toLowerCase().includes(query);
      })
      .sort((a, b) => b.checksMonth - a.checksMonth || b.completedChecks - a.completedChecks);
  }, [stats, search, activityFilter]);

  const getMetricCardSx = (color: string) => ({
    p: 3,
    height: '100%',
    borderRadius: 4,
    bgcolor: alpha(color, 0.08),
    border: '1px solid',
    borderColor: alpha(color, 0.25),
    boxShadow: `0 4px 20px ${alpha(color, 0.12)}`,
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
        <CircularProgress />
      </Box>
    );
  }

  const metricCards = [
    { label: 'Checks Today', value: stats?.checksToday ?? 0, helper: 'Completed today', color: theme.palette.info.main, icon: <TodayIcon /> },
    { label: 'This Week', value: stats?.checksThisWeek ?? 0, helper: 'Last 7 days', color: theme.palette.primary.main, icon: <DateRangeIcon /> },
    { label: 'This Month', value: stats?.checksThisMonth ?? 0, helper: 'Last 30 days', color: theme.palette.secondary.main, icon: <CalendarIcon /> },
    { label: 'All Time', value: stats?.totalChecks ?? 0, helper: 'Total completed', color: theme.palette.success.main, icon: <TrendingUpIcon /> },
    { label: 'Active (7d)', value: stats?.activeBusinesses7d ?? 0, helper: 'Businesses with checks', color: theme.palette.warning.main, icon: <BusinessIcon /> },
    { label: 'Active (30d)', value: stats?.activeBusinesses30d ?? 0, helper: 'Businesses with checks', color: theme.palette.error.main, icon: <AssessmentIcon /> },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Business Reports</Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Platform-wide activity, usage trends, and per-business breakdown.
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {metricCards.map(card => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={card.label}>
            <Card sx={getMetricCardSx(card.color)}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{ color: card.color }}>{card.icon}</Box>
                  <Typography variant="overline" color="text.secondary">{card.label}</Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{card.value.toLocaleString()}</Typography>
                <Typography variant="caption" color="text.secondary">{card.helper}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ p: 3, borderRadius: 3, boxShadow: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Checks Completed</Typography>
              <ToggleButtonGroup value={chartPeriod} exclusive onChange={(_, v) => v && setChartPeriod(v)} size="small">
                <ToggleButton value="daily">Daily</ToggleButton>
                <ToggleButton value="weekly">Weekly</ToggleButton>
                <ToggleButton value="monthly">Monthly</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {chartData.length === 0 ? (
              <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">No data for this period</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <ReTooltip contentStyle={{ background: theme.palette.background.paper, borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="Checks" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card sx={{ p: 3, borderRadius: 3, boxShadow: 2, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Check Types (30 days)</Typography>
            {!stats?.checksByType?.length ? (
              <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">No data yet</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={stats.checksByType.map(r => ({ name: r.type, value: r.count }))}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {stats.checksByType.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <ReTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, borderRadius: 3, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>New Businesses</Typography>
            {!stats?.newBusinesses?.length ? (
              <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">No data yet</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart
                  data={stats.newBusinesses.map(r => ({
                    label: formatChartDate(r.month, 'monthly'),
                    'New Businesses': r.count,
                  }))}
                  margin={{ top: 8, right: 8, bottom: 0, left: -12 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <ReTooltip contentStyle={{ background: theme.palette.background.paper, borderRadius: 8, fontSize: 12 }} />
                  <Line type="monotone" dataKey="New Businesses" stroke={theme.palette.secondary.main} strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, borderRadius: 3, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Top Businesses by Checks</Typography>
            {topBusinesses.length === 0 ? (
              <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">No data yet</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={topBusinesses} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
                  <ReTooltip contentStyle={{ background: theme.palette.background.paper, borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="checks" fill={theme.palette.success.main} radius={[0, 4, 4, 0]} maxBarSize={20} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* DVLA Checks Section */}
      <Card sx={{ p: 3, borderRadius: 2, boxShadow: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <SecurityIcon color="primary" />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>DVLA Checks by Business</Typography>
            <Typography variant="body2" color="text.secondary">
              Credit usage and check volume per business across the platform
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={getMetricCardSx(theme.palette.primary.main)}>
              <CardContent sx={{ p: 0 }}>
                <Typography variant="overline" color="text.secondary">Total DVLA Checks</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{(stats?.totalDvlaChecks ?? 0).toLocaleString()}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={getMetricCardSx(theme.palette.success.main)}>
              <CardContent sx={{ p: 0 }}>
                <Typography variant="overline" color="text.secondary">Completed</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{(stats?.completedDvlaChecks ?? 0).toLocaleString()}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={getMetricCardSx(theme.palette.error.main)}>
              <CardContent sx={{ p: 0 }}>
                <Typography variant="overline" color="text.secondary">Failed</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{(stats?.failedDvlaChecks ?? 0).toLocaleString()}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={getMetricCardSx(theme.palette.info.main)}>
              <CardContent sx={{ p: 0 }}>
                <Typography variant="overline" color="text.secondary">This Month</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{(stats?.dvlaChecksThisMonth ?? 0).toLocaleString()}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {topDvlaBusinesses.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Top Businesses by DVLA Checks</Typography>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={topDvlaBusinesses} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
                <ReTooltip contentStyle={{ background: theme.palette.background.paper, borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="checks" name="Completed checks" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]} maxBarSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>All Businesses — DVLA Usage</Typography>
          <TextField
            size="small"
            placeholder="Search businesses..."
            value={dvlaSearch}
            onChange={e => setDvlaSearch(e.target.value)}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
            }}
          />
        </Box>
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'background.default' }}>
              <TableRow>
                <TableCell>Business</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Today</TableCell>
                <TableCell align="right">7 Days</TableCell>
                <TableCell align="right">30 Days</TableCell>
                <TableCell align="right">Completed</TableCell>
                <TableCell align="right">Failed</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Credits Left</TableCell>
                <TableCell>Last DVLA Check</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDvlaBusinesses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">No businesses match your search.</TableCell>
                </TableRow>
              ) : (
                filteredDvlaBusinesses.map(b => (
                  <TableRow key={b.businessId} hover sx={{ opacity: b.totalDvlaChecks === 0 ? 0.65 : 1 }}>
                    <TableCell sx={{ fontWeight: 500 }}>{b.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={b.status || 'Unknown'}
                        size="small"
                        color={(b.status || '').toLowerCase() === 'active' ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{b.dvlaToday}</TableCell>
                    <TableCell align="right">{b.dvlaWeek}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: b.dvlaMonth > 0 ? 600 : 400 }}>{b.dvlaMonth}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{b.completedDvlaChecks.toLocaleString()}</TableCell>
                    <TableCell align="right">{b.failedDvlaChecks}</TableCell>
                    <TableCell align="right">{b.totalDvlaChecks.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      {b.remainingCredits != null ? b.remainingCredits.toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      {b.lastDvlaCheck
                        ? (() => { try { return format(new Date(b.lastDvlaCheck), 'dd MMM yyyy'); } catch { return '—'; } })()
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Card sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Business Activity</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Activity</InputLabel>
              <Select label="Activity" value={activityFilter} onChange={e => setActivityFilter(e.target.value as ActivityFilter)}>
                <MenuItem value="all">All businesses</MenuItem>
                <MenuItem value="active7d">Active (7 days)</MenuItem>
                <MenuItem value="active30d">Active (30 days)</MenuItem>
                <MenuItem value="inactive30d">Inactive (30+ days)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ minWidth: 220 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
              }}
            />
          </Box>
        </Box>
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'background.default' }}>
              <TableRow>
                <TableCell>Business</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Today</TableCell>
                <TableCell align="right">7 Days</TableCell>
                <TableCell align="right">30 Days</TableCell>
                <TableCell align="right">All Time</TableCell>
                <TableCell>Last Active</TableCell>
                <TableCell>Joined</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBusinesses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">No businesses match your filters.</TableCell>
                </TableRow>
              ) : (
                filteredBusinesses.map(b => (
                  <TableRow key={b.businessId} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{b.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={b.status || 'Unknown'}
                        size="small"
                        color={(b.status || '').toLowerCase() === 'active' ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{b.checksToday}</TableCell>
                    <TableCell align="right">{b.checksWeek}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: b.checksMonth > 0 ? 600 : 400 }}>{b.checksMonth}</TableCell>
                    <TableCell align="right">{b.completedChecks.toLocaleString()}</TableCell>
                    <TableCell>
                      {b.lastActive ? (() => { try { return format(new Date(b.lastActive), 'dd MMM yyyy'); } catch { return '—'; } })() : '—'}
                    </TableCell>
                    <TableCell>
                      {b.joinedAt ? (() => { try { return format(new Date(b.joinedAt), 'dd MMM yyyy'); } catch { return '—'; } })() : '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Container>
  );
};

export default BusinessReports;
